import React, { useEffect, useState } from 'react'
import { ApolloClient } from '@apollo/client'
import classNames from 'classnames'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { useGuildMember } from '@/services/GuildUserService'
import { ContentWithWatchableReasons, PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { fetchSeriesEpisodeStills } from '@/services/ProjectsService'
import { EpisodeWithStills } from '@/services/ProjectsService/types'
import { ShopifyStore } from '@/types/codegen-contentful'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { buildCloudinaryImagePathFromContentfulObject, getCloudinaryImageUrl } from '@/utils/Cloudinary/cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { GetInvolvedSquare } from '@/views/WatchTheatricalView/GetInvolvedSquare'
import { useWatchTheatricalContext } from '@/views/WatchTheatricalView/WatchTheatricalContext'
import {
  CallToAction,
  filterToApplicableCtas,
  formatColumnClassName,
  formatCtaClassName,
  formatNumColumns,
  injectMissingCtas,
} from './utils'

interface CtaSquareCollectionProps {
  numberAvailable?: number
  client: ApolloClient
  currentPhaseSlug?: PhaseSlugForTracking
  ticketStatus?: TicketsStatusReference | null
  /** Content with watchableReasons for streaming availability checks */
  watchableContent?: ContentWithWatchableReasons | null
  /** Whether the project is currently streaming, from page-level data */
  isStreaming?: boolean
}

export const CtaSquareCollection: React.FC<CtaSquareCollectionProps> = ({
  numberAvailable,
  client,
  currentPhaseSlug,
  ticketStatus = null,
  watchableContent,
  isStreaming = false,
}) => {
  const [episodesWithStills, setEpisodesWithStills] = useState<EpisodeWithStills[] | null>(null)

  const { t } = useTranslate('theatrical-presales')
  const projectMeta = useProjectMeta()
  const { ctaSquaresCollection, slug, shopifyStore } = useWatchTheatricalContext()
  const { isGuildMember } = useGuildMember()
  const pifEnabled = projectMeta?.pifEnabled

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (projectMeta?.projectType === 'series' && projectMeta?.slug) {
        const eps = await fetchSeriesEpisodeStills(client, projectMeta.slug)
        if (!cancelled) setEpisodesWithStills(eps)
      } else {
        if (!cancelled) setEpisodesWithStills(null)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [client, projectMeta?.projectType, projectMeta?.slug])

  const backdropPath = projectMeta?.title?.landscapeBackdrop?.cloudinaryPath
  const titleStills = projectMeta?.title
    ? {
        still1: projectMeta.title.still1,
        still2: projectMeta.title.still2,
        still3: projectMeta.title.still3,
        still4: projectMeta.title.still4,
      }
    : null
  const rawCtas = (ctaSquaresCollection?.items as CallToAction[] | null) || []
  const augmented = injectMissingCtas(
    rawCtas,
    backdropPath,
    t,
    watchableContent,
    isGuildMember,
    slug,
    projectMeta?.name,
    pifEnabled,
    numberAvailable,
    shopifyStore as ShopifyStore | null | undefined,
    episodesWithStills || undefined,
    ticketStatus,
    titleStills,
    isStreaming,
  )
  const ctas = filterToApplicableCtas(augmented, isGuildMember)
  if (ctas.length === 0) return null

  return (
    <div
      id="get-involved"
      className={classNames('grid grid-cols-1', formatColumnClassName(formatNumColumns(ctas.length)))}
    >
      {ctas.map((cta) => {
        return (
          <GetInvolvedSquare
            key={cta.title}
            backgroundImg={
              cta.customCta && cta?.backgroundImageUrl
                ? getCloudinaryImageUrl({ path: cta.backgroundImageUrl })
                : buildCloudinaryImagePathFromContentfulObject(cta.backgroundImage)
            }
            icon={
              cta.customCta && cta?.iconImageUrl
                ? getCloudinaryImageUrl({ path: cta.iconImageUrl })
                : buildCloudinaryImagePathFromContentfulObject(cta.icon)
            }
            title={cta.title}
            description={cta.description}
            buttonText={isGuildMember ? cta?.guildButtonText ?? cta.buttonText : cta.buttonText}
            buttonHref={isGuildMember ? cta?.guildButtonHref ?? cta.buttonHref : cta.buttonHref}
            emailListId={cta.emailListId}
            slug={slug}
            type={cta.type}
            className={formatCtaClassName(formatNumColumns(ctas.length))}
            currentPhaseSlug={currentPhaseSlug}
          />
        )
      })}
    </div>
  )
}
